import MoneyWithCurrencyIcon from '@/components/biz/MoneyWithCurrencyIcon';
import BonusCountDown from '@/components/biz/rewards/bonusCountDown';
import WrappedButton from '@/components/ui/WrappedButton';
import { Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box, Stack } from '@mui/system';
import { confirm } from 'material-ui-confirm';

import {
  ActivityStatusEnum,
  ActivityTypeEnum,
  cancelSubActivity,
  enableSubActivity,
  TUserActivity,
} from '@/apis/promotions';
import monthlyIcon from '@/components/biz/rewards/assets/monthly.png';
import { TermsAndConditionsLink } from '@/components/features/common/TermsAndConditions';
import useTheme from '@/theme/useTheme';
import { useCountDown, useRequest } from 'ahooks';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
// import MoonEventView from '@/components/biz/promotions/weekly/thursday';
import SaturdayTAndC from '@/components/biz/promotions/weekly/saturday-t-and-c';
import ThursdayTAndC from '@/components/biz/promotions/weekly/thursday-t-and-c';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import { Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { ThursdayOrSaturdayTAndC } from '@/components/biz/promotions/weekly/helpers';

const Img = styled('img')(({ theme }) => ({
  width: '42%',
  height: 'auto',
  margin: '0 auto',
}));

const FreeSpinBlock: React.FC<{
  activity: TUserActivity;
  refresh: () => void;
}> = ({ activity, refresh }) => {
  const { status, activityTitle, endTime, activityType, countdownTime } =
    activity;
  const [t] = useTranslation();
  const { push } = useRouter();

  const handleToast = (data: any) => {
    if (data?.success) {
      toast.success(t('Success'));
      refresh();
    } else {
      toast.error(data?.message);
    }
  };

  const { runAsync: enableActivity, loading } = useRequest(
    () => {
      return enableSubActivity(activity.recordId);
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.success === false) {
          toast.error(data.message);
          refetch();
        } else {
          handleToast(data);
          setTimeout(() => {
            push(`/casino/play/${activity.freeBetTargetGameId}`);
          }, 1000);
        }
      },
    },
  );

  const [timeLeft, { hours, minutes, seconds }] = useCountDown({
    targetDate: Number(countdownTime),
    onEnd: refresh,
  });

  const buttonText =
    status >= 1
      ? 'Resume'
      : timeLeft > 0
        ? `Available in ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        : 'Get Free Spin';

  const { theme } = useTheme();

  const [tAndCModalOpen, setTAndCModalOpen] = React.useState(false);

  const { data: activityQueue, refetch } = useQuery<TUserActivity[]>(
    queryKeysFactory.rewards.activityQueue(),
  );

  const otherOnGoingActivity =
    activityQueue.find(
      (a) => a.activityType !== activityType && a.status > 0,
    ) ||
    activityQueue.find(
      (a) => a.activityType === ActivityTypeEnum.WELCOME_BONUS,
    );

  const { runAsync: cancelActivity, loading: loadingCancel } = useRequest(
    () => {
      return cancelSubActivity(activity.recordId);
    },
    {
      manual: true,
      onSuccess: handleToast,
    },
  );

  const tAndText = (
    <ThursdayOrSaturdayTAndC
      type={
        activityType === ActivityTypeEnum.THURSDAY_FIRST_FREE_SPIN ||
        activityType === ActivityTypeEnum.THUDSDAY_SECOND_FREE_SPIN
          ? ActivityTypeEnum.THURSDAY
          : ActivityTypeEnum.SATURDAY
      }
    />
  );

  const mask =
    otherOnGoingActivity && status === ActivityStatusEnum.NOT_VALID ? (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1,
          display: 'grid',
          placeItems: 'center',
          padding: 2,
          textAlign: 'center',
          fontWeight: 500,
        }}
        borderRadius={1}
      >
        <Box>
          <Trans
            i18nKey={
              otherOnGoingActivity.activityType === ActivityTypeEnum.RELOAD
                ? 'You have a deposit bonus in progress. Please complete your Deposit Bonus first to get this Free Spin bonus Please check the <terms>Terms & Conditions</terms>'
                : 'You have Free Spins in progress. Please complete your bonus first to get this Free Spin bonus Please check the <terms>Terms & Conditions</terms>'
            }
            components={{
              terms: tAndText,
            }}
          ></Trans>
        </Box>
      </Box>
    ) : null;

  return (
    <Stack
      direction={'column'}
      padding={3}
      gap={1}
      position={'relative'}
      borderRadius={1}
    >
      {mask}
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={2}
      >
        <Typography fontSize={16} color={'text.secondary'}>
          {activityTitle}
        </Typography>
        <WrappedButton
          isLoading={loadingCancel}
          onClick={() => {
            confirm({
              title: t('rewards::Cancel Free Spin'),
              description: t(
                'rewards::You will not be able to claim the winnings obtained from the Free Spin. Are you sure you want to cancel?',
              ),
              confirmationText: t('rewards::Yes'),
              cancellationText: t('rewards::No'),
            }).then(() => {
              cancelActivity();
            });
          }}
          size="small"
          variant="text"
          endIcon={<Close />}
        >
          {t('Cancel')}
        </WrappedButton>
      </Stack>
      <Img src={monthlyIcon.src} alt="monthly" />
      <Typography color={'text.secondary'}>
        You've won: <MoneyWithCurrencyIcon value={activity.frozenAmount} /> in
        free spin
      </Typography>
      {status < 2 && (
        <>
          <Typography color={'text.secondary'}>
            Free Spins Available: {activity.freeSpinCount}
          </Typography>
          {tAndText}
        </>
      )}

      {Number(activity.frozenAmount) > 0 ? (
        <Typography color={'text.secondary'}>
          <Trans
            i18nKey={
              'Please complete your wagering requirements to claim the winning from Free Spin. You can find out more in our <terms>Terms & Conditions</terms>'
            }
            components={{
              terms: tAndText,
            }}
          ></Trans>
        </Typography>
      ) : null}
      {status < 5 && status >= 1 && (
        <>
          <Typography textAlign={'center'}>
            Wagered to unlock:{' '}
            <MoneyWithCurrencyIcon value={activity.currentWagerAmount} /> /{' '}
            <MoneyWithCurrencyIcon value={activity.targetWagerAmount} />
          </Typography>
          <BonusCountDown
            titleText="Expire in"
            countdown={activity.endTime}
          ></BonusCountDown>
        </>
      )}
      {status <= 0 && (
        <Divider
          sx={{
            borderColor: 'transparent',
            my: 4,
            flex: 1,
          }}
        />
      )}
      {Number(activity.frozenAmount) > 0 ? (
        <WrappedButton href={status >= 1 ? `/casino` : undefined}>
          {t('rewards::Play Games')}
        </WrappedButton>
      ) : status === -1 && timeLeft <= 0 ? (
        <WrappedButton disabled>{t('Unavailable')}</WrappedButton>
      ) : status === 5 ? (
        <WrappedButton disabled>
          {t('rewards::Rewards Distributed')}
        </WrappedButton>
      ) : (
        <WrappedButton
          onClick={() => {
            status === 0 && enableActivity();
          }}
          isLoading={loading}
          href={
            status >= 1
              ? `/casino/play/${activity.freeBetTargetGameId}`
              : undefined
          }
          disabled={timeLeft > 0}
        >
          {buttonText}
        </WrappedButton>
      )}
      {/* {(status === -1 || status === 0) && (
          <WrappedButton
            disabled={status !== 0}
            isLoading={loading}
            onClick={() => {
              enableActivity();
            }}
          >
            {getButtontext(status, t)}
          </WrappedButton>
        )} */}
      {/* {status >= 1 && (
          <WrappedButton href={`/casino/play/${activity.freeBetTargetGameId}`}>
            {t('Play Games')}
          </WrappedButton>
        )} */}
    </Stack>
  );
};

export default FreeSpinBlock;
